import React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography } from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FormattedMessage } from "react-intl";

interface ExportNameModalProps {
    open: boolean;
    onClose: () => void;
    onCancel: () => void;
    onConfirm: (fileName?: string) => void;
}
interface ExportNameForm {
    fileName: string | null;
}

// Messages are the English fallback (docs/i18n-conventions.md §3). The modal builds the id
// `export.name-modal.file-name.error.<type>` from the yup error type, so the catalog wins whenever the key exists.
const exportNameSchema = yup.object({
    fileName: yup
        .string()
        .trim()
        .max(100, "File name cannot exceed 100 characters.")
        .matches(/^[^<>:"/\\|?*]*$/, 'File name cannot contain any of the following characters: < > : " / \\ | ? *')
        .nullable()
        .default(null),
});

const ExportNameModal: React.FC<ExportNameModalProps> = ({ open, onClose, onConfirm, onCancel }) => {
    const { control, handleSubmit, reset } = useForm<ExportNameForm>({
        resolver: yupResolver(exportNameSchema),
        defaultValues: { fileName: null },
    });

    const handleCancel = () => {
        reset();
        onCancel();
    };

    const onSubmit = (data: { fileName?: string | null }) => {
        onConfirm(data?.fileName ?? undefined);
        reset();
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
            <DialogTitle>
                <FormattedMessage id="export.label.enter-name" defaultMessage="Export File Name" />
            </DialogTitle>
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <DialogContent dividers>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        <FormattedMessage id="export.name-modal.info.text" defaultMessage="You can optionally provide a file name for the export." />
                    </Typography>
                    <Controller
                        name="fileName"
                        control={control}
                        render={({ field: { value, onChange, ...field }, fieldState: { invalid, error } }) => (
                            <TextField
                                {...field}
                                fullWidth
                                size="medium"
                                variant="outlined"
                                label={<FormattedMessage id="export.label.name-modal-file-name" defaultMessage="File Name" />}
                                value={value ?? ""}
                                onChange={(e) => onChange(e.target.value)}
                                error={invalid}
                                helperText={
                                    invalid && error ? (
                                        <FormattedMessage id={`export.name-modal.file-name.error.${error.type}`} defaultMessage={error.message} />
                                    ) : (
                                        " "
                                    )
                                }
                                slotProps={{
                                    htmlInput: { maxLength: 100 },
                                }}
                            />
                        )}
                    />
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleCancel}>
                        <FormattedMessage id="common.button-label.cancel" defaultMessage="Cancel" />
                    </Button>
                    <Button type="submit">
                        <FormattedMessage id="common.button-label.confirm" defaultMessage="Confirm" />
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default ExportNameModal;
